import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FormikProvider, useFormik } from 'formik'

import { PayrollSetupList } from './PayrollSetupList'
import {
  selectFirstEmployee,
  selectFirstEmployeeJob,
} from '../payroll.selectors'
import {
  postCreateEmployeeJob,
  fetchEmployees,
  putUpdateEmployeeJob,
  putUpdateJobCompensation,
  putUpdateEmployeeHomeAddress,
  fetchEmployeeHomeAddresses,
  PUT_UPDATE_EMPLOYEE_HOME_ADDRESS_KEY,
  PUT_UPDATE_EMPLOYEE_JOB_KEY,
  POST_CREATE_EMPLOYEE_JOB_KEY,
  PUT_UPDATE_JOB_COMPENSATION_KEY,
  postCreateEmployeeHomeAddress,
} from '../payrollActions'
import GustoAddress from '../GustoAddress'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import {
  flsaDropdownOptions,
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useEmployeeWorkLocations,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { FlsaStatus, PaymentUnit } from '../employeeJob.slice'
import { PayrollError } from '../PayrollError'
import { Button, Card, Link, Separator, Form, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Gusto_EmployeeHomeAddress } from '../generated_gusto_types'
import { WorkAddressModal } from '../settings/WorkAddressModal'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'
// TODO: Migrate to V2
import { LabelDescription } from '@/componentsV1/BaseComponents/Input'
import {
  getFieldNames,
  makeDateSchema,
  makeNumberSchema,
  makeReqStringSchema,
} from '@/base'

const JobForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const homeAddressRef = useRef<GustoAddress>(null)
  const [addAddressModal, setAddAddressModal] = useState(false)

  const employee = useReselector(selectFirstEmployee)
  const job = useReselector(selectFirstEmployeeJob)
  const [homeAddress, setHomeAddress] =
    useState<Gusto_EmployeeHomeAddress | null>()

  const { locationOptions, defaultAddress, setWorkLocation } =
    useEmployeeWorkLocations(employee?.uuid)

  useEffect(() => {
    const fetch = async () => {
      if (!employee?.uuid) {
        return
      }
      const allHomeAddresses = await dispatch(
        fetchEmployeeHomeAddresses(employee.uuid)
      )

      setHomeAddress(
        allHomeAddresses?.find((address) => address.active) || null
      )
    }

    fetch()
  }, [dispatch, employee?.uuid])

  const formik = useFormik({
    initialValues: {
      hire_date: job?.hire_date
        ? moment(job?.hire_date, DATE_FORMATS.GUSTO_SUBMIT).format(
            DATE_FORMATS.INPUT
          )
        : '',
      job_title: job?.title || 'Owner',
      rate: job?.compensations[0]?.rate || '0',
      work_location: defaultAddress || '',
      flsa_status: job?.compensations[0]?.flsa_status || FlsaStatus.Exempt,
      payment_unit: job?.compensations[0]?.payment_unit || PaymentUnit.Year,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!employee?.uuid) {
        return
      }

      const setWorkAddressSuccess = await setWorkLocation(values.work_location)
      if (!setWorkAddressSuccess) {
        return
      }

      // Set home address
      const address = homeAddressRef.current?.getAddressWithoutPhone()

      if (!address) {
        return
      }

      let homeAddressRes

      if (!homeAddress) {
        homeAddressRes = await postCreateEmployeeHomeAddress(
          employee.uuid,
          address
        )(dispatch)
      } else {
        homeAddressRes = await putUpdateEmployeeHomeAddress(homeAddress.uuid, {
          ...address,
          version: homeAddress.version,
        })(dispatch)
      }

      if (!homeAddressRes) {
        return
      }

      // Set here to update version
      setHomeAddress(homeAddressRes)

      // Set Job
      let compensationUuid
      let compensationVersion

      if (job?.uuid) {
        const res = await putUpdateEmployeeJob(job.uuid, {
          version: job.version,
          title: values.job_title,
          hire_date: moment(values.hire_date, DATE_FORMATS.INPUT).format(
            DATE_FORMATS.GUSTO_SUBMIT
          ),
        })(dispatch)

        if (!res) {
          return
        }

        compensationUuid = res.compensations[0]?.uuid
        compensationVersion = res.compensations[0]?.version
      } else {
        const res = await postCreateEmployeeJob(employee.uuid, {
          title: values.job_title,
          hire_date: moment(values.hire_date, DATE_FORMATS.INPUT).format(
            DATE_FORMATS.GUSTO_SUBMIT
          ),
        })(dispatch)

        if (!res) {
          return
        }
        compensationUuid = res.compensations[0]?.uuid
        compensationVersion = res.compensations[0]?.version
      }

      // Set compensation
      if (!compensationUuid || !compensationVersion) {
        return
      }

      const res = await putUpdateJobCompensation(compensationUuid, {
        rate: values.rate,
        payment_unit: values.payment_unit,
        version: compensationVersion,
        flsa_status: values.flsa_status,
      })(dispatch)

      if (res) {
        track(
          'completed payroll step - employee job creation, compensation creation, work and home address setting'
        )

        navigate(GEP_ENROLL_PATHS.employeeFederal)
      }
    },
  })

  const { isSubmitting, submitForm } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H2}>
          Your Job and Compensation details
        </Text>

        <Text>
          (The questions below may feel confusing, which is why we have
          pre-filled some fields with our recommendations). If you have
          questions, you can reach out via{' '}
          <Link href="/conversations">Messages</Link>
        </Text>

        <Separator />

        <Text variant={TypographyStyles.H2} className="mt-4">
          Add yourself as an employee
        </Text>

        <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_HOME_ADDRESS_KEY} />
        <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_JOB_KEY} />
        <PayrollError fetchKey={POST_CREATE_EMPLOYEE_JOB_KEY} />
        <PayrollError fetchKey={PUT_UPDATE_JOB_COMPENSATION_KEY} />

        <Form.Input
          name={fieldNames.job_title}
          label="Job Title"
          required
          schema={makeReqStringSchema({ field: 'job title' })}
        />

        <Form.DateInput
          name={fieldNames.hire_date}
          required
          label="Start Date"
          description="We recommend that your start date should be the day you enroll in payroll (today). If not, you may be required to file prior payroll taxes."
          schema={makeDateSchema({ field: 'hire date' })}
        />

        <LabelDescription
          required
          label="Employee Type"
          description={
            <Text>
              We recommend Salary/No Overtime as your employee classification.{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/7479624872855"
                newPage
              >
                Help me with this
              </Link>
            </Text>
          }
        />

        <div className="w-full md:w-5/6">
          <Form.Select
            name={fieldNames.flsa_status}
            options={flsaDropdownOptions}
            schema={makeReqStringSchema({ field: 'employee type' })}
          />
        </div>

        <LabelDescription
          required
          label="Compensation"
          description={
            <Text>
              We recommend setting a reasonable salary at around 40% of your
              expected income this year.{' '}
              <Link
                href="https://support.joinheard.com/hc/en-us/articles/7475592736919"
                newPage
              >
                What is a reasonable salary and why does it matter?
              </Link>
            </Text>
          }
        />

        <div className="flex flex-col md:flex-row gap-4 items-center md:items-end">
          <div className="w-full md:w-1/2">
            <Form.Input
              name={fieldNames.rate}
              componentType="currency"
              required
              schema={makeNumberSchema({ field: 'rate', allowedDecimals: 2 })}
            />
          </div>
          <Text className="hidden md:block pb-3">Per</Text>
          <div className="w-full md:w-1/2">
            <Form.Select
              name={fieldNames.payment_unit}
              options={Object.values(PaymentUnit)
                .filter((name) => name !== 'Paycheck')
                .map((name) => ({
                  text: name,
                  value: name,
                }))}
              required
              schema={makeReqStringSchema({ field: 'payment unit' })}
            />
          </div>
        </div>

        {homeAddress !== undefined && (
          <div className="w-full">
            <LabelDescription label="What is your home address?" />
            <GustoAddress
              ref={homeAddressRef}
              {...homeAddress}
              hasPhoneNumber={false}
            />
          </div>
        )}

        <Form.Select
          name={fieldNames.work_location}
          options={locationOptions}
          label="What is your primary working address?"
          description="If you work from home, this would be your home address."
          schema={makeReqStringSchema({ field: 'Work Location' })}
        />

        <Text>
          Location missing?{' '}
          <Button variant="link" onClick={() => setAddAddressModal(true)}>
            Add work address here
          </Button>
        </Text>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4">
          <div className="w-full md:w-1/3">
            <Button
              variant="secondary"
              onClick={() => navigate(GEP_ENROLL_PATHS.employee)}
            >
              Back
            </Button>
          </div>
          <div className="w-full md:w-1/3 md:ml-auto">
            <Button onClick={submitForm} loading={isSubmitting}>
              Save & Continue
            </Button>
          </div>
        </div>
      </div>

      {addAddressModal && (
        <WorkAddressModal
          open={addAddressModal}
          closeModal={() => setAddAddressModal(false)}
        />
      )}
    </FormikProvider>
  )
}

export const PayrollEnrollEmployeeJob = () => {
  const dispatch = useAppDispatch()
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.employeeJob)

  useEffect(() => {
    dispatch(fetchEmployees())
  }, [dispatch])

  return (
    <div>
      <Card>
        <div className="flex flex-col gap-6">
          <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>

          <Separator />

          <PrefilledNoticeContainer />

          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2">
              <JobForm />
            </div>
            <div className="w-full md:w-1/2">
              <PayrollSetupList />
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
